import { TrackControllerRecommendationSearchRequest } from '@cratehackers/api-client';
import { IonButton, IonText } from '@ionic/react';
import { GridReadyEvent } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import React, { useCallback, useEffect, useState } from 'react';
import ApiService from '../../services/Api.service';
import { useDispatch } from '../../store/hooks';
import { setSelectedTrack } from '../../store/slices/track.slice';
import { iTrack } from '../../types/ITrack';
import './Recommendations.scss';
import WaveformInline from '../waveform/WaveformInline';
import { useBreakpoint } from '../../context/breakpoint/BreakpointContext';
import { cleanTitle, cleanArtist } from '../../utils/cleanTrackMetadata';

interface RecommendationsProps extends iTrack {
  showAddTrack?: boolean;
  addTrackHandler?: (trackId: string) => void;
}

export const Recommendations: React.FC<RecommendationsProps> = (props) => {
  const { showAddTrack = false, addTrackHandler, ...track } = props;
  const [recommendations, setRecommendations] = useState<iTrack[]>([]);
  const [gridApi, setGridApi] = useState<any>();
  const { matchesMediumDown } = useBreakpoint();

  const dispatch = useDispatch();

  interface IAgGridProp {
    value: string;
  }

  const handleAddTrack = useCallback(
    (value: string) => {
      props.addTrackHandler?.(value);
    },
    [props.addTrackHandler],
  );

  const keyCategoryOrder: {
    [key in
      | 'PerfectMatch'
      | 'EnergyBoost'
      | 'EnergyDrop'
      | 'MoodChange'
      | 'Compatible']: number;
  } = {
    PerfectMatch: 1,
    EnergyBoost: 2,
    EnergyDrop: 3,
    MoodChange: 4,
    Compatible: 5,
  };

  const getKeyCategory = (
    key: string,
  ): keyof typeof keyCategoryOrder | null => {
    for (const category in track.keyMatches) {
      if (
        Array.isArray(track.keyMatches[category]) &&
        track.keyMatches[category].includes(key)
      ) {
        return category as keyof typeof keyCategoryOrder;
      }
    }
    return null;
  };

  const formatKeyCategory = (category: string) => {
    return category
      .split(/(?=[A-Z])/)
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  const KeyMatchCellRenderer: React.FC<IAgGridProp> = ({ value }) => {
    if (!value) return null;
    const keyCategory = getKeyCategory(value);
    return (
      <div className={'key'}>
        {value}
        {value.length === 2 && <>&nbsp;</>}
        {keyCategory && (
          <span className={`key-${keyCategory}`}>
            &nbsp;({formatKeyCategory(keyCategory)})
          </span>
        )}
      </div>
    );
  };

  const WaveformRenderer: React.FC<IAgGridProp> = ({ value }) => {
    return (
      <>
        {value ? (
          <div style={{ paddingTop: '12px' }}>
            <WaveformInline height={14} url={value} />
          </div>
        ) : (
          <IonText
            style={{
              opacity: '40%',
              paddingLeft: '0%',
              display: 'flex',
              alignItems: 'center',
              height: '100%',
            }}
          >
            No Preview
          </IonText>
        )}
      </>
    );
  };

  const AddTrackCellRenderer: React.FC<IAgGridProp> = ({ value }) => {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100%',
        }}
      >
        <IonButton
          fill="clear"
          size="small"
          style={{ whiteSpace: 'nowrap' }}
          onClick={() => handleAddTrack(value)}
        >
          + Add
        </IonButton>
      </div>
    );
  };

  useEffect(() => {
    if (gridApi) {
      gridApi.refreshCells({ force: true });
    }
  }, [recommendations, gridApi, track]);

  const [columnDefs] = useState([
    {
      headerName: '',
      field: 'ID',
      hide: !showAddTrack,
      cellRenderer: AddTrackCellRenderer,
      filter: false,
      sortable: false,
      width: 90,
      maxWidth: 90,
      cellStyle: { paddingLeft: 0, paddingRight: 0 } as any,
    },
    {
      field: 'Title',
      headerName: 'Title',
      filter: false,
      cellStyle: { paddingTop: '10px' } as any,
      sortable: false,
    },
    {
      field: 'Artist',
      headerName: 'Artist',
      filter: false,
      cellStyle: { paddingTop: '10px' } as any,
      sortable: false,
    },
    {
      field: 'BPM',
      headerName: 'BPM',
      sortable: true,
      cellStyle: { paddingTop: '10px' } as any,
      filter: false,
      width: 80,
      maxWidth: 80,
    },
    {
      field: 'Key_Camelot',
      headerName: 'Key',
      filter: false,
      cellStyle: { paddingTop: '10px' } as any,
      sortable: false,
      width: 160,
      maxWidth: 160,
      comparator: (
        valueA: string,
        valueB: string,
        nodeA: any,
        nodeB: any,
        isDescending: boolean,
      ) => {
        const categoryA = getKeyCategory(valueA);
        const categoryB = getKeyCategory(valueB);
        const orderA = categoryA ? keyCategoryOrder[categoryA] : 5;
        const orderB = categoryB ? keyCategoryOrder[categoryB] : 5;
        return orderA - orderB;
      },
      sort: 'asc' as 'asc' | 'desc' | undefined,
      cellRenderer: KeyMatchCellRenderer,
    },
    {
      field: 'PreviewURL',
      headerName: 'Preview',
      sortable: true,
      filter: false,
      cellStyle: { paddingRight: '10px' } as any,
      cellRenderer: WaveformRenderer,
      width: 120,
    },
  ]);

  const onGridReady = (params: GridReadyEvent) => {
    setGridApi(params.api);
    setTimeout(() => {
      gridApi?.showLoadingOverlay();
    });
  };

  const cellClickedListener = (event: any) => {
    if (!showAddTrack) {
      dispatch(setSelectedTrack(event.data));
    }
  };

  useEffect(() => {
    const chApi = ApiService.getApiClient();
    const requestParams: TrackControllerRecommendationSearchRequest = {
      spotifySearchDto: {
        term: `${track.Title} ${track.Artist}`,
      },
    };
    chApi
      .trackControllerRecommendationSearch(requestParams)
      .then(async (response) => {
        const tracks = response
          .map(
            (responseTrack: any): iTrack => ({
              Artist: responseTrack.artists
                .map((artist: any) => artist.name)
                .join(', '),
              Title: responseTrack.name,
              AlbumCover: responseTrack.album.imageUrl,
              Key_Camelot: responseTrack.keys.camelot,
              Key: responseTrack.keys.standard,
              PreviewURL: responseTrack.previewUrl,
              BPM: Math.round(responseTrack.bpm),
              ID: responseTrack.id,
              popularity: responseTrack.popularity,
              keyMatches: {
                energyBoost: responseTrack.keys.energyBoost,
                energyDrop: responseTrack.keys.energyDrop,
                moodChange: responseTrack.keys.moodChange,
                perfectMatch: responseTrack.keys.perfectMatch,
              },
            }),
          )
          .filter((filterTrack) => {
            const isSameId =
              filterTrack.ID === track.ID ||
              (track.SpotifyID && filterTrack.ID === track.SpotifyID);
            if (isSameId) return false;

            const cleanFilterArtist = (
              cleanArtist(filterTrack.Artist) || ''
            ).toLowerCase();
            const cleanFilterTitle = (
              cleanTitle(filterTrack.Title) || ''
            ).toLowerCase();
            const cleanTargetArtist = (
              cleanArtist(track.Artist) || ''
            ).toLowerCase();
            const cleanTargetTitle = (
              cleanTitle(track.Title) || ''
            ).toLowerCase();

            return !(
              cleanFilterArtist === cleanTargetArtist &&
              cleanFilterTitle === cleanTargetTitle
            );
          });
        if (
          (track.keyMatches === undefined ||
            track.keyMatches.energyBoost === undefined ||
            track.keyMatches.moodChange === undefined ||
            track.keyMatches.perfectMatch === undefined ||
            track.keyMatches.energyDrop === undefined ||
            track.keyMatches.compatible === undefined) &&
          track.Key_Camelot
        ) {
          try {
            track.keyMatches = await chApi.keysControllerGetKeyMatches({
              keyDto: {
                camelot: track.Key_Camelot,
                standard: track.Key ? String(track.Key) : '',
              },
            });
          } catch (error) {
            // An unrecognised key should not cost us the whole grid.
            console.error(
              `Could not load key matches for ${track.Key_Camelot}`,
              error,
            );
          }
        }
        setRecommendations(tracks);
        if (gridApi) {
          if (!matchesMediumDown) {
            gridApi.sizeColumnsToFit();
          }
          gridApi.hideOverlay();
        }
      });
  }, [track.Artist, track.Title, gridApi]);

  return (
    <>
      <h2>Recommendations</h2>
      <div className={matchesMediumDown ? 'recommendations-grid-wrapper' : ''}>
        <AgGridReact
          rowData={recommendations}
          columnDefs={columnDefs}
          onGridReady={onGridReady}
          onCellClicked={(event: any) => {
            !['PreviewURL'].includes(event.colDef.field) &&
              cellClickedListener(event);
          }}
          rowStyle={{ cursor: 'pointer' }}
          domLayout="autoHeight"
          localeText={{ noRowsToShow: 'No Recommendations Found!' }}
          defaultColDef={{ sortable: true }}
        />
      </div>
    </>
  );
};
